import React from "react";
import Main from "layouts/Main";
import { addDocument, modifyDataset, removeDatasetFile } from "services/api";
import { Box, Button, Divider, IconButton, InputAdornment, LinearProgress, List, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import FileUploader from "components/FileUploader";
import { AddCircleOutline, Close, Done, Error, WarningRounded } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { DatasetFile, isDatasetFile } from "types/dataset";
import { useTranslation } from "react-i18next";
import Loading from "components/Loading";
import { useConfirmDialog } from "components/ConfirmDialog";
import Container from "components/Container";

type Progress = "none" | "loading" | "success" | "error";
type UploadFile = File | DatasetFile | string;

const AddDocument = () => {
	const { id } = useParams();
	const { t } = useTranslation(["common"]);
	const confirmDialog = useConfirmDialog();

	const [datasetName, setDatasetName] = React.useState("");

	const [urlLink, setUrlLink] = React.useState("");
	const [urlError, setUrlError] = React.useState("");

	const [files, setFiles] = React.useState<Array<UploadFile>>([]);
	const [progresses, setProgresses] = React.useState<Array<Progress>>([]);
	const [errorMessage, setErrorMessage] = React.useState<Array<string>>([]);

	const [isProgressing, setIsProgressing] = React.useState(false);
	const [isLoading, setLoading] = React.useState(false);

	const uploadable = React.useMemo(() => {
		return files.filter((file) => !isDatasetFile(file)).length > 0;
	}, [files]);

	const handleUrlAdd = (url: string) => {
		try {
			new URL(url);
			handleAdd([url]);
		} catch (e) {
			setUrlError(t("invaildUrl", { ns: "validation" }));
		}
	};
	const handleAdd = (files: Array<File | string>) => {
		setFiles((prev) => [...prev, ...files]);
	};

	const getTitle = (file: UploadFile) => {
		if (typeof file === "string") return file;
		if (file instanceof File) return file.name;
		return file.title;
	};
	const handleUpload = () => {
		if (isProgressing) return;
		setIsProgressing(true);
		const newProress = files.map((file, i) => {
			if (progresses[i] === "success" || isDatasetFile(file)) return "success";
			return "loading";
		});
		setProgresses(newProress);

		const maxFileSize = 100 * 1024 * 1024;

		Promise.all(
			files.map((file, i) => {
				// skip uploaded file
				if (isDatasetFile(file) || newProress[i] !== "loading") return null;

					// check max file size
					if (file instanceof File && file.size > maxFileSize) {
						setErrorMessage((prev) => {
							const newErrors = [...prev];
							newErrors[i] = "exceededFileSize";
							return newErrors;
						});
						setProgresses((prev) => {
							const newProgresses = [...prev];
							newProgresses[i] = "error";
							return newProgresses;
						});
						return null;
					}

					// upload file
					return addDocument(id, file as File | string)
						.then((resp) => {
							setFiles((prev) => {
								const newFiles = [...prev];
								newFiles[i] = { fileId: resp.fileId, title: getTitle(file) };
								return newFiles;
							});
							setProgresses((prev) => {
								const newProgresses = [...prev];
								newProgresses[i] = "success";
								return newProgresses;
							});
						})
						.catch((err) => {
							setErrorMessage((prev) => {
								const newErrors = [...prev];
								newErrors[i] = err?.message || "uploadFailed";
								return newErrors;
							});
							setProgresses((prev) => {
								const newProgresses = [...prev];
								newProgresses[i] = "error";
								return newProgresses;
							});
						});
				})
			).finally(() => {
				setIsProgressing(false);
			});
		};

	const removeFile = (index: number) => {
		setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
		setProgresses((prevProgresses) => prevProgresses.filter((_, i) => i !== index));
		setErrorMessage((prev) => prev.filter((_, i) => i !== index));
	};
	const onRemove = (index: number) => {
		removeDatasetFile(id, (files[index] as DatasetFile).fileId).then((resp) => {
			if (resp.info) {
				removeFile(index);
			}
		});
	};
	const handleRemove = (index: number) => {
		if (progresses[index] === "success") {
			confirmDialog.openDialog(t("deleteDocument"), t("deleteDocumentMessage"), () => onRemove(index));
		} else {
			removeFile(index);
		}
	};

	React.useEffect(() => {
		if (id) {
			setLoading(true);
			modifyDataset(id, "getInfo", {}).then((resp) => {
				if (!resp || resp?.error) return;
				setDatasetName(resp.name);
			});
			modifyDataset(id, "getFileList", {})
				.then((resp) => {
					if (!resp || resp?.error) return;
					setFiles(resp.files);
					setProgresses(new Array(resp.files.length).fill("success"));
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, []);

	return (
		<Main title={t("addDocument")}>
			{isLoading ? (
				<Loading />
			) : (
				<Container mt={2}>
					<Typography
						variant="h4"
						component={"h2"}>
						{datasetName}
					</Typography>
					<Box
						width={1}
						mt={1}
						mb={3}>
						<Divider />
					</Box>
					<FileUploader
						accept=".csv, .json, .xml, .pdf, .ppt, .pptx, .doc, .docx, .xls, .xlsx, .html, .md, .txt"
						multiple
						handleUpload={handleAdd}
					/>
					<Divider sx={{ my: 1 }}>{t("or")}</Divider>
					<TextField
						label={t("url")}
						fullWidth
						variant="outlined"
						placeholder="https://xxxxxxxxxx"
						value={urlLink}
						error={Boolean(urlError)}
						helperText={urlError}
						onChange={(e) => setUrlLink(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleUrlAdd(urlLink);
								setUrlLink("");
							}
						}}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												handleUrlAdd(urlLink);
												setUrlLink("");
											}}
											size="small"
											sx={{ color: "text.secondary" }}>
											<AddCircleOutline />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<List dense>
						{files.map((file, i) => {
							const status = progresses[i];
							const name = file instanceof File ? file.name : typeof file === "string" ? file : file.title;
							return (
								<ListItem
									key={i}
									secondaryAction={
										<IconButton
											size="small"
											disabled={status === "loading"}
											onClick={() => handleRemove(i)}
											sx={{ color: "text.secondary" }}>
											<Close />
										</IconButton>
									}>
									<ListItemText>
										<Box sx={{ display: "flex", alignItems: "center" }}>
											{status === "success" && <Done color={"success"} />}
											{status === "error" && <Error color={"error"} />}
											<Typography
												noWrap
												color={status === "success" ? "success" : status === "error" ? "error" : "inherit"}>
												{name}
											</Typography>
										</Box>
										{status === "loading" && <LinearProgress />}
										{status === "error" && errorMessage[i] && <Typography color="error">{t(errorMessage[i])}</Typography>}
									</ListItemText>
								</ListItem>
							);
						})}
					</List>
					{isProgressing && (
						<Typography
							color="warning"
							sx={{ mb: 1, display: "flex", alignItems: "center" }}>
							<WarningRounded sx={{ height: 16, weight: 16 }} />
							{t("uploadRemindMessage")}
						</Typography>
					)}

					<Box
						display={uploadable ? "flex" : "none"}
						alignItems="center"
						gap={1}>
						<Button
							variant="contained"
							color="error"
							disabled={isProgressing}
							onClick={() =>
								setFiles(
									files.filter((_, i) => {
										return progresses[i] === "success";
									})
								)
							}>
							{t("clear")}
						</Button>
						<Button
							variant="contained"
							disabled={isProgressing}
							onClick={handleUpload}>
							{t("upload")}
						</Button>
					</Box>
				</Container>
			)}
		</Main>
	);
};

export default AddDocument;
